export const BOOK_DEMO_URL = "https://wa.me/6587169250";
export const number = (value: number) =>
  new Intl.NumberFormat("en-SG").format(value);

export const journeyStages = [
  {
    name: "Acquisition",
    audience: "New customers",
    title: "Start the relationship with context.",
    description:
      "Bring new enquiries and customer details together, so your team can respond with context from the start.",
  },
  {
    name: "Conversion",
    audience: "Engaged customers",
    title: "Help interest become a next step.",
    description:
      "Use conversation history and customer context to prepare relevant follow-ups that help customers move forward.",
  },
  {
    name: "Retention",
    audience: "Converted customers",
    title: "Keep the relationship going.",
    description:
      "Use past interactions and timely follow-ups to stay connected after the first conversion.",
  },
] as const;

// Billing periods on the pricing toggle. Prices below are per month on each.
export const billingPeriods = [
  { id: "monthly", label: "Monthly", months: 1, saving: null },
  { id: "quarterly", label: "Quarterly", months: 3, saving: "Save 10%" },
  { id: "yearly", label: "Yearly", months: 12, saving: "Save 20%" },
] as const;

export type BillingPeriodId = (typeof billingPeriods)[number]["id"];

// Approved marketing offer (co-founder pricing plan, 2026-10-24). These are
// NOT Stripe IDs or checkout entitlements.
export const offers = [
  {
    id: "starter",
    name: "Starter",
    audience: "For single studios just starting out",
    monthly: 200,
    quarterly: 180,
    yearly: 160,
    customers: 300,
    storage: 1000,
    branches: 1,
    staff: 0,
    extra: 90,
    ceiling: 500,
    scope: "1 branch · 1 owner account",
    features: [
      "Personalised customer profiles",
      "Templated workflows",
      "App integrations",
      "Analytics dashboard",
    ],
    featured: false,
  },
  {
    id: "growth",
    name: "Growth",
    audience: "For fast-scaling teams with 1–2 branches",
    monthly: 450,
    quarterly: 400,
    yearly: 360,
    customers: 1000,
    storage: 2500,
    branches: 2,
    staff: 5,
    extra: 70,
    ceiling: 1200,
    scope: "2 branches · 5 staff accounts",
    features: [
      "Ask Dibs AI",
      "AI-inferred customer attributes",
      "Custom workflows",
      "Custom MCP/API connections",
    ],
    featured: true,
  },
  {
    id: "premium",
    name: "Premium",
    audience: "For established businesses with multiple branches",
    monthly: 600,
    quarterly: 540,
    yearly: 480,
    customers: 1500,
    storage: 15000,
    branches: 5,
    staff: 15,
    extra: 60,
    ceiling: 12000,
    scope: "5 branches · 15 staff accounts",
    features: [
      "All Growth capabilities",
      "More room for customer relationships",
      "Lower-cost engagement packs",
      "Greater team capacity",
    ],
    featured: false,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    audience: "For large organisations",
    monthly: null,
    quarterly: null,
    yearly: null,
    customers: null,
    storage: null,
    branches: null,
    staff: null,
    extra: null,
    ceiling: null,
    scope: "Unlimited branches and staff",
    features: [
      "Custom engagement capacity",
      "Unlimited contact storage",
      "Custom workflows",
      "Custom MCP/API connections",
    ],
    featured: false,
  },
] as const;

export type ComparisonRow = { group: string; label: string; values: string[] };
const all = ["Included", "Included", "Included", "Included"];
const teams = ["Not included", "Included", "Included", "Included"];
// This is the actual rendered comparison, also exported by PricingSection for existing imports.
export const comparisonRows: ComparisonRow[] = [
  {
    group: "Customer capacity",
    label: "Monthly engaged customers",
    values: offers.map((p) =>
      p.customers === null ? "Custom" : number(p.customers),
    ),
  },
  {
    group: "Customer capacity",
    label: "Additional monthly engaged customers",
    values: offers.map((p) =>
      p.extra === null ? "Custom" : `S$${p.extra} / 100 customers / month`,
    ),
  },
  {
    group: "Customer capacity",
    label: "Maximum monthly engaged customers",
    values: offers.map((p) =>
      p.ceiling === null ? "Custom" : number(p.ceiling),
    ),
  },
  {
    group: "Customer capacity",
    label: "Stored contacts (inactive customers)",
    values: offers.map((p) =>
      p.storage === null ? "Unlimited" : number(p.storage),
    ),
  },
  {
    group: "Customer profiles",
    label: "Personalised contact profiles",
    values: all,
  },
  {
    group: "Customer profiles",
    label: "Additional file and text inputs",
    values: all,
  },
  {
    group: "Customer profiles",
    label: "Attribute types",
    values: [
      "Given, computed",
      "Given, computed, AI-inferred",
      "Given, computed, AI-inferred",
      "Given, computed, AI-inferred",
    ],
  },
  { group: "Workflows", label: "Templated workflows", values: all },
  { group: "Workflows", label: "Custom workflows", values: teams },
  { group: "Connections", label: "App integrations", values: all },
  { group: "Connections", label: "Custom MCP/API", values: teams },
  { group: "Insights", label: "Analytics dashboard", values: all },
  { group: "Insights", label: "Ask Dibs AI", values: teams },
  {
    group: "Team controls",
    label: "Branches",
    values: offers.map((p) =>
      p.branches === null ? "Unlimited" : String(p.branches),
    ),
  },
  {
    group: "Team controls",
    label: "Owner / staff accounts",
    values: ["1 owner; no additional staff", "5", "15", "Unlimited"],
  },
];

export const faqs = [
  [
    "What does Dibs help my business do?",
    "Dibs brings customer information, conversations, and follow-ups together, helping your team keep context and spend less time coordinating repeat work.",
  ],
  [
    "How are monthly engaged customers counted?",
    "Your monthly engaged-customer allowance is separate from inactive customers saved in Dibs. We’ll walk through which interactions count and how the usage period works during your demo.",
  ],
  [
    "What happens if I need to engage with more customers?",
    "Every plan can add packs of 100 customers a month: S$90 on Starter (up to 500 monthly engaged customers in total), S$70 on Growth (up to 1,200), and S$60 on Premium (up to 12,000). Enterprise capacity is agreed individually.",
  ],
  [
    "Can I review messages before they are sent?",
    "Workflows can include an approval step before a message is sent. The illustrative example on this page shows that setup.",
  ],
  [
    "Can I pay quarterly or yearly?",
    "Yes. Starter, Growth, and Premium can be billed monthly, quarterly, or yearly. Quarterly billing saves about 10% and yearly billing saves 20% compared with paying monthly.",
  ],
  [
    "Which channels can I connect?",
    "Dibs connects to WhatsApp. We can walk through other integrations and the requirements for your setup during the demo.",
  ],
  [
    "What happens during a demo?",
    "See how Dibs works and discuss your customer journey, requirements, and selected plan with us.",
  ],
] as const;
